import { requiredMessage } from '@/constants/error';
import { DatePicker, DatePickerProps, Input, InputNumber } from 'antd';
import { validateIdNumber, validateMobileRegex } from '@/utils/validateUtils';
import { currencyFormat } from '@/utils/commonUtils';
import { IHireInformationResponse, IOption, Options } from '@/models/employee';
import { DATE_FORMAT, EMPLOYMENT_TYPE } from '@/constants/employee';
import {
  USER_ENROLLMENT_DISABLED_DAYS_AFTER_HIRE_DATE,
  USER_ENROLLMENT_DISABLED_DAYS_BEFORE_HIRE_DATE
} from '@/constants/date';
import moment from 'moment/moment';
import { Rule } from 'rc-field-form/lib/interface';
import { ReactElement } from 'react';
import CustomSelect from '@/components/custom-select/CustomSelect';
import CustomDatePicker from '@/components/custom-date-picker/CustomDatePicker';
import CustomRadioGroup from '@/components/custom-radio-group/CustomRadioGroup';

interface ComponentProps {
  optionKey?: string;
  showSearch?: boolean;
  optionFilter?: (options: IOption[], formValues: IHireInformationResponse) => IOption[];
}

interface FieldComponentFun<T> {
  (config: T, options:Options, getFormFieldsValue: () => IHireInformationResponse): ReactElement;
}

export interface HireFieldConfig {
  colSpan: number;
  name: string;
  label: string;
  rules: Rule[];
  component: ReactElement | FieldComponentFun<HireFieldConfig>
  componentProps?: ComponentProps
  visibleCondition?: (formValues: IHireInformationResponse) => boolean;
  validateTrigger?: string | string[];
}

const SALE_POSITIONS = [ 'Business Development Manager', 'Client Principal', 'Sales Lead','Market Tech Director' ];
const payFieldVisibleCondition = formValues => {
  return SALE_POSITIONS.includes(formValues.position);
};

const isEmployeeType = formValues => formValues.employmentType === EMPLOYMENT_TYPE.EMPLOYEE;

const disabledDate: DatePickerProps['disabledDate'] = current => {
  const availableEndDay = USER_ENROLLMENT_DISABLED_DAYS_AFTER_HIRE_DATE - moment().get('day');
  const availableStartDay = -USER_ENROLLMENT_DISABLED_DAYS_BEFORE_HIRE_DATE;
  return current &&
      (current < moment().add(availableStartDay, 'day').startOf('day') ||
          current > (moment().add(availableEndDay, 'day').startOf('day')));
};

export const fieldValidatorConfig: Array<HireFieldConfig[]> = (
  [
    [
      {
        colSpan: 8,
        name: 'legalFullName',
        label: 'Legal Name',
        rules: [ { required: true, message: requiredMessage } ],
        component: <Input maxLength={100}/>
      },
      {
        colSpan: 8,
        name: 'chineseName',
        label: '姓名',
        rules: [ { required: true, message: requiredMessage } ],
        component: <Input maxLength={100}/>
      }
    ],
    [
      {
        colSpan: 12,
        name: 'position',
        label: 'Position',
        rules: [ { required: true, message: requiredMessage } ],
        component: CustomSelect,
        componentProps: {
          optionKey: 'positionOptions',
          optionFilter: (options, formValues) => {
            return isEmployeeType(formValues) ? options : options.filter(option => !SALE_POSITIONS.includes(option.value as string));
          },
        },
      },
      {
        colSpan: 8,
        name: 'positionCn',
        label: '岗位',
        rules: [ { required: true, message: requiredMessage } ],
        component: CustomSelect,
        componentProps: {
          optionKey: 'positionCnOptions'
        }
      },
    ],
    [
      {
        colSpan: 8,
        name: 'hireDate',
        label: '入职日期',
        rules: [ { required: true, message: requiredMessage } ],
        component: CustomDatePicker(false, disabledDate)
      },
      {
        colSpan: 8,
        name: 'homeOffice',
        label: 'Home Office',
        rules: [ { required: true, message: requiredMessage } ],
        component: CustomSelect,
        componentProps: {
          optionKey: 'homeOfficeOptions'
        }
      },
      {
        colSpan: 8,
        name: 'homeOffice',
        label: '办公地点',
        rules: [ { required: true, message: requiredMessage } ],
        component: CustomSelect,
        componentProps: {
          optionKey: 'homeOfficeCnOptions'
        }
      },
    ],
    [
      {
        colSpan: 8,
        name: 'nationId',
        label: 'Nationality',
        rules: [ { required: true, message: requiredMessage } ],
        component: CustomSelect,
        componentProps: {
          optionKey: 'nationOptions'
        }
      },
      {
        colSpan: 8,
        name: 'nationId',
        label: '国籍',
        rules: [ { required: true, message: requiredMessage } ],
        component: CustomSelect,
        componentProps: {
          optionKey: 'nationCnOptions'
        }
      },
    ],
    [
      {
        colSpan: 8,
        name: 'idType',
        label: '证件类型',
        rules: [ { required: true, message: requiredMessage } ],
        component: CustomSelect,
        componentProps: {
          optionKey: 'idTypesOptions'
        }
      },
      {
        colSpan: 8,
        name: 'idNumber',
        label: '证件号码',
        rules: [ { required: true, message: requiredMessage }, validateIdNumber ],
        validateTrigger: 'onBlur',
        component: <Input maxLength={100}/>
      },
      {
        colSpan: 8,
        name: 'phoneNumber',
        label: '手机号码',
        rules: [ { required: true, message: requiredMessage }, validateMobileRegex ],
        component: <Input disabled/>
      },
    ],
    [
      {
        colSpan: 4,
        name: 'currency',
        label: 'Currency',
        rules: [ { required: true, message: requiredMessage } ],
        component: <Input disabled={true}/>,
        visibleCondition: isEmployeeType
      },
      {
        colSpan: 5,
        name: 'annualSalary',
        label: 'Annual Salary',
        rules: [ { required: true, message: requiredMessage } ],
        component: <InputNumber
          disabled={true}
          min={0}
          controls={false}
          precision={2}
          formatter={currencyFormat}
        />,
        visibleCondition: isEmployeeType
      },
      {
        colSpan: 4,
        name: 'currencyCn',
        label: '货币',
        rules: [ { required: true, message: requiredMessage } ],
        component: <Input disabled={true}/>,
        visibleCondition: isEmployeeType
      },
      {
        colSpan: 11,
        name: 'annualSalaryCn',
        label: '年薪',
        rules: [ { required: true, message: requiredMessage } ],
        component: <Input disabled={true}/>,
        visibleCondition: isEmployeeType
      },
    ],
    [
      {
        colSpan: 4,
        name: 'basePay',
        label: 'Base Pay',
        rules: [ { required: true, message: requiredMessage } ],
        component: <InputNumber
          disabled={true}
          min={0}
          controls={false}
          precision={2}
          formatter={currencyFormat}
        />,
        visibleCondition: payFieldVisibleCondition
      },
      {
        colSpan: 11,
        name: 'basePayCn',
        label: '基本薪资',
        rules: [ { required: true, message: requiredMessage } ],
        component: <Input disabled={true}/>,
        visibleCondition: payFieldVisibleCondition
      },
    ],
    [
      {
        colSpan: 4,
        name: 'variablePay',
        label: 'Variable Pay',
        rules: [ { required: true, message: requiredMessage } ],
        component: <InputNumber
          disabled={true}
          min={0}
          controls={false}
          precision={2}
          formatter={currencyFormat}
        />,
        visibleCondition: payFieldVisibleCondition
      },
      {
        colSpan: 11,
        name: 'variablePayCn',
        label: '浮动薪资',
        rules: [ { required: true, message: requiredMessage } ],
        component: <Input disabled={true}/>,
        visibleCondition: payFieldVisibleCondition
      },
    ],
    [
      {
        colSpan: 12,
        name: 'homeAddress',
        label: 'Home Address',
        rules: [ { required: true, message: requiredMessage } ],
        component: <Input.TextArea showCount maxLength={180}/>
      },
      {
        colSpan: 12,
        name: 'homeAddressCn',
        label: '居住地址',
        rules: [ { required: true, message: requiredMessage } ],
        component: <Input.TextArea showCount maxLength={90}/>
      }
    ],
    [
      {
        colSpan: 24,
        name: 'hireType',
        label: '招聘类型',
        rules: [ { required: true, message: requiredMessage } ],
        component: CustomRadioGroup,
        componentProps: {
          optionKey: 'hiredTypesOptions'
        }
      }
    ],
    [
      {
        colSpan: 24,
        name: 'trainingType',
        label: '培训类型(非必选)',
        rules: [],
        component: CustomRadioGroup,
        componentProps: {
          optionKey: 'trainingTypesOptions'
        },
        visibleCondition: isEmployeeType
      }
    ],
    [
      {
        colSpan: 12,
        name: 'trainingDateRange',
        label: '培训时间',
        rules: [ { required: true, message: requiredMessage } ],
        component: <DatePicker.RangePicker getPopupContainer={triggerNode => triggerNode} picker="date" format={DATE_FORMAT}/>,
        visibleCondition: formValues => !!formValues.trainingType && isEmployeeType(formValues)
      },
      {
        colSpan: 8,
        name: 'trainingLocation',
        label: '培训地点',
        rules: [ { required: true, message: requiredMessage } ],
        component: CustomSelect,
        visibleCondition: formValues => !!formValues.trainingType && isEmployeeType(formValues),
        componentProps: {
          optionKey: 'trainingTypeLocationMappings',
          showSearch: false,
          optionFilter: (options, formValues) => {
            return options.filter(option => formValues.trainingType === option.text)
              .map(option => ({ text: option.value, value: option.value } as IOption));
          },
        }
      },
    ],
    [
      {
        colSpan: 24,
        name: 'hasWorkExperience',
        label: '入职前是否有全职工作经历?',
        rules: [ { required: true, message: requiredMessage } ],
        component: CustomRadioGroup,
        componentProps: {
          optionKey: 'hasWorkExperienceOption'
        }
      }
    ],
  ]
);